import { createContext, useContext, ReactNode } from 'react';
import { JmapClient } from '@/services/jmapClient';

interface JmapContextValue {
  /** Bearer token for the current authenticated session. */
  token: string | null;
  /** Singleton JMAP client. Reused across the whole app. */
  client: JmapClient | null;
  /** JMAP account id for the primary mail account. */
  accountId: string | null;
}

const JmapContext = createContext<JmapContextValue>({
  token: null,
  client: null,
  accountId: null,
});

interface JmapProviderProps {
  token: string | null;
  client: JmapClient | null;
  accountId: string | null;
  children: ReactNode;
}

/**
 * Provides a single JMAP client + account id to the entire authenticated app.
 *
 * Before this, every page (Mailbox, Calendar, Contacts, Settings, Drive,
 * Signatures) constructed its own `new JmapClient(token)`, called
 * `getSession()`, then `getMailboxes()`. That made every navigation feel
 * like a fresh page load — three round-trips and a flicker per route.
 *
 * Pages should now do:
 *   const { client, accountId } = useJmap();
 * and use those directly. App.tsx owns the lifecycle.
 */
export function JmapProvider({ token, client, accountId, children }: JmapProviderProps) {
  return (
    <JmapContext.Provider value={{ token, client, accountId }}>
      {children}
    </JmapContext.Provider>
  );
}

/**
 * Consume the shared JMAP client and account id. Returns nulls until the
 * provider has resolved them — pages should guard with `if (!client || !accountId)`.
 */
export function useJmap(): JmapContextValue {
  return useContext(JmapContext);
}
